import type {
  KundaliRequest,
  KundaliResponse,
  DoshaResult,
  PlanetPosition,
  HouseData,
  RashiInfo,
  MatchingRequest,
  MatchingResponse,
  DetailedMatchingResponse,
} from "@ishvaram/api-client";
import { getGoogleAnalyticsClientId } from "@/lib/analytics-ga4";
import { getOrCreateBootstrapDistinctId as getAnalyticsDistinctId } from "@/lib/analytics-identity";
import { API_BASE, fetchWithTimeout, throwApiError } from "./_fetch";
import { newIdempotencyKey, RetryableHttpError, withResilientRetry } from "./_retry";

export type {
  KundaliRequest,
  KundaliResponse,
  DoshaResult,
  PlanetPosition,
  HouseData,
  RashiInfo,
  MatchingRequest,
  MatchingResponse,
  DetailedMatchingResponse,
};
export type { PersonInput } from "@ishvaram/api-client";

const MAX_KUNDALI_ATTEMPTS = 3;

export { RetryExhaustedError } from "./_retry";

export interface GenerateKundaliOptions {
  /**
   * One idempotency key per logical browser submit. Reused across every retry
   * attempt so a flaky-network re-send cannot double-create the reading
   * server-side. A manual "Try again" should pass the SAME key. Defaults to a
   * fresh key when omitted (single-shot callers).
   */
  idempotencyKey?: string;
}

/**
 * Generate a kundali with the resilient-submit standard (`_retry.ts`):
 * full-jitter exponential backoff, one idempotency key reused across attempts,
 * 4xx never retried, and a typed `RetryExhaustedError` on exhaustion so the UI
 * renders a user-facing "Try again" state instead of a dead-end string.
 *
 * 30s timeout per attempt because /kundali does Swiss Ephemeris + Gemini LLM
 * (18-22s cold-start). 2026-05 funnel data showed conversion dropping from 83%
 * to 0% when the bulk of failures surfaced as native "TypeError: Failed to fetch"
 * from Indian ISPs flaking on direct calls — full jitter spreads those retries.
 */
export async function generateKundali(
  data: KundaliRequest,
  options: GenerateKundaliOptions = {}
): Promise<KundaliResponse> {
  const idempotencyKey = options.idempotencyKey ?? newIdempotencyKey();
  return withResilientRetry<KundaliResponse>(
    async (key) => {
      const res = await fetchWithTimeout(
        `${API_BASE}/kundali`,
        {
          method: "POST",
          headers: { ...kundaliAnalyticsHeaders(), "Idempotency-Key": key },
          body: JSON.stringify(data),
        },
        30_000
      );
      if (!res.ok) {
        // 4xx → terminal (throwApiError surfaces the field message); 5xx →
        // RetryableHttpError so withResilientRetry backs off and re-sends.
        if (res.status >= 400 && res.status < 500) await throwApiError(res);
        throw new RetryableHttpError(res.status, `kundali ${res.status}`);
      }
      return res.json() as Promise<KundaliResponse>;
    },
    {
      maxAttempts: MAX_KUNDALI_ATTEMPTS,
      idempotencyKey,
    }
  );
}

export interface KundaliPrefill {
  name: string;
  birth_date: string;
  birth_time: string;
  birth_place: string;
  latitude: number | null;
  longitude: number | null;
  timezone: string;
  /** Captured contact on the reading — threads into Razorpay prefill so a
   * nudge clicker (whose phone we literally messaged) is never re-asked. */
  contact_email?: string;
  contact_phone?: string;
}

/**
 * Fetch ONLY the stored birth inputs for a saved reading (GET /kundali/{id}/prefill)
 * to prefill the kundali form for a one-tap re-entry from a reactivation nudge — the
 * seeker's chart is already on file, so they confirm + pay instead of re-typing.
 * Best-effort: a rejection means "no prefill, the user types it" (today's behavior).
 */
export async function getKundaliPrefill(readingId: string): Promise<KundaliPrefill> {
  const res = await fetchWithTimeout(
    `${API_BASE}/kundali/${encodeURIComponent(readingId)}/prefill`,
    { method: "GET", headers: { "Content-Type": "application/json" } },
    // 20s tolerates a cold Cloud Run start (~10-17s right after a deploy) so a
    // nudge landing reliably prefills instead of silently falling back to empty.
    20_000
  );
  if (!res.ok) throw new Error(`Could not load prefill (${res.status})`);
  return res.json() as Promise<KundaliPrefill>;
}

function kundaliAnalyticsHeaders(): Record<string, string> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (typeof window === "undefined") return headers;
  const posthogDistinctId = getAnalyticsDistinctId();
  const gaClientId = getGoogleAnalyticsClientId();
  if (posthogDistinctId) headers["X-PostHog-Distinct-Id"] = posthogDistinctId;
  if (gaClientId) headers["X-GA-Client-Id"] = gaClientId;
  headers["X-Ishvaram-Source-Path"] = window.location.pathname || "/free-kundali";
  return headers;
}

export async function listRashis(): Promise<RashiInfo[]> {
  const res = await fetchWithTimeout(`${API_BASE}/rashifal`);
  if (!res.ok) throw new Error("Failed to fetch rashis");
  return res.json();
}

export async function calculateMatching(data: MatchingRequest): Promise<MatchingResponse> {
  const res = await fetchWithTimeout(`${API_BASE}/matching`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function calculateDetailedMatching(
  data: MatchingRequest
): Promise<DetailedMatchingResponse> {
  const res = await fetchWithTimeout(`${API_BASE}/matching/detailed`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function downloadMatchingReport(
  data: MatchingRequest & { language?: string }
): Promise<Blob> {
  const res = await fetchWithTimeout(`${API_BASE}/reports/matching`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      person_a: data.person_a,
      person_b: data.person_b,
      language: data.language || "en",
    }),
  });
  if (!res.ok) await throwApiError(res, "Report generation failed");
  return res.blob();
}
